import { useState, useRef, useEffect, useCallback } from 'react';
import { calcularPontosFinanceiro } from '../../utils/pontosFinanceiro';
import { useBottomSheet } from '../../hooks/useBottomSheet';

interface PagamentoContaModalProps {
  isOpen: boolean;
  onClose: () => void;
  saldoDisponivel?: number;
  pontosDisponiveis?: number;
  onPagamentoRealizado?: (
    valor: number,
    beneficiario: string,
    tipo: string,
    tipoTransacao: 'pagamento_conta'
  ) => void;
}

export function PagamentoContaModal({
  isOpen,
  onClose,
  saldoDisponivel,
  pontosDisponiveis,
  onPagamentoRealizado,
}: PagamentoContaModalProps) {
  const saldo = saldoDisponivel || pontosDisponiveis || 0;
  const [tipoConta, setTipoConta] = useState<'boleto' | 'consumo'>('boleto');
  const [codigoBarras, setCodigoBarras] = useState('');
  const [dadosConta, setDadosConta] = useState<any>(null);
  const [etapa, setEtapa] = useState<'formulario' | 'token' | 'sucesso'>('formulario');
  const [processando, setProcessando] = useState(false);
  const [validando, setValidando] = useState(false);
  const [creditoSelecionado, setCreditoSelecionado] = useState<string>('');
  const [tokenDigits, setTokenDigits] = useState<string[]>(['', '', '', '', '', '']);
  const [tokenEnviado, setTokenEnviado] = useState(false);
  const [tokenErro, setTokenErro] = useState('');
  const [reenvioTimer, setReenvioTimer] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [salvandoPdf, setSalvandoPdf] = useState(false);
  const [shareMenuOpen, setShareMenuOpen] = useState(false);
  const [copiado, setCopiado] = useState(false);
  const dataHoraTransacao = useRef('');

  // Créditos disponíveis que compõem o saldo
  const creditosDisponiveis = [
    {
      id: 'credito-1',
      descricao: 'Bônus Meta de Vendas',
      campanha: 'Verão 2024',
      valor: 2000.0,
      data: '24/12/2025',
    },
    {
      id: 'credito-2',
      descricao: 'Comissão Vendas Janeiro',
      campanha: 'Programa Anual 2025',
      valor: 1500.0,
      data: '15/01/2025',
    },
    {
      id: 'credito-3',
      descricao: 'Premiação Trimestral',
      campanha: 'Q4 2024',
      valor: 850.0,
      data: '10/01/2025',
    },
    {
      id: 'credito-4',
      descricao: 'Bônus Indicação',
      campanha: 'Programa Amigos',
      valor: 500.0,
      data: '05/01/2025',
    },
  ];

  const creditoAtual = creditosDisponiveis.find((c) => c.id === creditoSelecionado);

  // Pontos necessários calculados dinamicamente com base no valor da conta
  const valorConta = dadosConta?.valor ?? 0;
  const pontosNecessarios = calcularPontosFinanceiro(valorConta);

  // Timer para reenvio do token
  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;
    if (reenvioTimer > 0) {
      interval = setInterval(() => {
        setReenvioTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [reenvioTimer]);

  // Focar no primeiro input ao entrar na etapa token
  useEffect(() => {
    if (etapa === 'token' && inputRefs.current[0]) {
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    }
  }, [etapa]);

  const getComprovanteTexto = useCallback(() => {
    if (!dadosConta) return '';
    const dataHora = dataHoraTransacao.current || new Date().toLocaleString('pt-BR');
    return [
      '✅ Comprovante de Pagamento de Conta',
      '',
      `Beneficiário: ${dadosConta.beneficiario}`,
      `Tipo: ${dadosConta.tipo}`,
      creditoAtual ? `Crédito: ${creditoAtual.descricao}` : '',
      `Valor: R$ ${dadosConta.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      `Data: ${dataHora}`,
    ].filter(Boolean).join('\n');
  }, [dadosConta, creditoAtual]);

  const gerarComprovantePDF = useCallback(async () => {
    if (!dadosConta) return;
    
    setSalvandoPdf(true);
    
    // Simula geração do PDF
    await new Promise((resolve) => setTimeout(resolve, 1500));
    
    // Cria um elemento de link temporário para download
    const element = document.createElement('a');
    const texto = getComprovanteTexto();
    const blob = new Blob([texto], { type: 'text/plain' });
    element.href = URL.createObjectURL(blob);
    element.download = `comprovante-pagamento-${Date.now()}.txt`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
    
    setSalvandoPdf(false);
  }, [dadosConta, getComprovanteTexto]);

  const handleShareWhatsApp = useCallback(() => {
    const texto = getComprovanteTexto();
    const url = `https://wa.me/?text=${encodeURIComponent(texto)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    setShareMenuOpen(false);
  }, [getComprovanteTexto]);

  const handleShareTelegram = useCallback(() => {
    const texto = getComprovanteTexto();
    const url = `https://t.me/share/url?text=${encodeURIComponent(texto)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    setShareMenuOpen(false);
  }, [getComprovanteTexto]);

  const handleShareFacebook = useCallback(() => {
    const texto = getComprovanteTexto();
    const url = `https://www.facebook.com/sharer/sharer.php?quote=${encodeURIComponent(texto)}`;
    window.open(url, '_blank', 'noopener,noreferrer,width=600,height=400');
    setShareMenuOpen(false);
  }, [getComprovanteTexto]);

  const handleShareTwitter = useCallback(() => {
    const texto = getComprovanteTexto();
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(texto)}`;
    window.open(url, '_blank', 'noopener,noreferrer,width=600,height=400');
    setShareMenuOpen(false);
  }, [getComprovanteTexto]);

  const handleShareLinkedIn = useCallback(() => {
    const texto = getComprovanteTexto();
    const url = `https://www.linkedin.com/sharing/share-offsite/?summary=${encodeURIComponent(texto)}`;
    window.open(url, '_blank', 'noopener,noreferrer,width=600,height=400');
    setShareMenuOpen(false);
  }, [getComprovanteTexto]);

  const handleCopiarTexto = useCallback(async () => {
    const texto = getComprovanteTexto();
    try {
      await navigator.clipboard.writeText(texto);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      // fallback silencioso
    }
    setShareMenuOpen(false);
  }, [getComprovanteTexto]);

  const handleFechar = useCallback(() => {
    setEtapa('formulario');
    setTipoConta('boleto');
    setCodigoBarras('');
    setDadosConta(null);
    setCreditoSelecionado('');
    setTokenDigits(['', '', '', '', '', '']);
    setTokenEnviado(false);
    setTokenErro('');
    setReenvioTimer(0);
    onClose();
  }, [onClose]);

  const { mounted, sheetClass, backdropClass, handleClose: animatedClose } = useBottomSheet({ isOpen, onClose: handleFechar });

  if (!mounted) return null;

  const handleValidarCodigo = async () => {
    if (codigoBarras.length < 44) return;

    setValidando(true);
    await new Promise((resolve) => setTimeout(resolve, 1500));

    const valorConta = creditoAtual ? creditoAtual.valor : 150.0;

    setDadosConta({
      beneficiario: tipoConta === 'boleto' ? 'Loja Exemplo LTDA' : 'Companhia de Energia Elétrica',
      valor: valorConta,
      vencimento: '2024-02-15',
      tipo: tipoConta === 'boleto' ? 'Boleto Bancário' : 'Conta de Consumo - Energia',
      documento: codigoBarras,
    });

    setValidando(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!dadosConta) return;
    setTokenEnviado(true);
    setReenvioTimer(60);
    setTokenDigits(['', '', '', '', '', '']);
    setTokenErro('');
    setEtapa('token');
  };

  const handleTokenChange = (index: number, value: string) => {
    if (value && !/^\d$/.test(value)) return;

    const newDigits = [...tokenDigits];
    newDigits[index] = value;
    setTokenDigits(newDigits);
    setTokenErro('');

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleTokenKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !tokenDigits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleTokenPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pastedData.length > 0) {
      const newDigits = [...tokenDigits];
      for (let i = 0; i < pastedData.length && i < 6; i++) {
        newDigits[i] = pastedData[i];
      }
      setTokenDigits(newDigits);
      const focusIndex = Math.min(pastedData.length, 5);
      inputRefs.current[focusIndex]?.focus();
    }
  };

  const handleReenviarToken = () => {
    if (reenvioTimer > 0) return;
    setReenvioTimer(60);
    setTokenDigits(['', '', '', '', '', '']);
    setTokenErro('');
    inputRefs.current[0]?.focus();
  };

  const tokenCompleto = tokenDigits.every((d) => d !== '');

  const handleConfirmarToken = async () => {
    if (!tokenCompleto) return;

    setProcessando(true);
    await new Promise((resolve) => setTimeout(resolve, 2000));

    const tokenValue = tokenDigits.join('');
    if (tokenValue === '000000') {
      setTokenErro('Token inválido. Verifique e tente novamente.');
      setProcessando(false);
      return;
    }

    if (onPagamentoRealizado && dadosConta) {
      onPagamentoRealizado(dadosConta.valor, dadosConta.beneficiario, dadosConta.tipo, 'pagamento_conta');
    }

    dataHoraTransacao.current = new Date().toLocaleString('pt-BR');
    setProcessando(false);
    setEtapa('sucesso');
  };

  const handleCodigoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    setCodigoBarras(value);
    setDadosConta(null);
  };

  const valorValido = dadosConta && creditoAtual && dadosConta.valor <= creditoAtual.valor;

  return (
    <div
      className={`fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center sm:p-4 z-50 ${backdropClass}`}
      onClick={animatedClose}
    >
      <div
        className={`bg-white sm:rounded-2xl rounded-t-2xl max-w-md w-full flex flex-col ${sheetClass}`}
        style={{ maxHeight: 'calc(100dvh - 64px)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white p-6 relative">
          <button
            onClick={animatedClose}
            className="absolute top-4 right-4 w-10 h-10 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <i className="ri-bill-line text-2xl"></i>
            </div>
            <div>
              <h3 className="text-2xl font-bold">Pagamento de Conta</h3>
              <p className="text-sm opacity-90">
                {etapa === 'formulario' && 'Pague boletos e contas de consumo'}
                {etapa === 'token' && 'Confirme com o token enviado'}
                {etapa === 'sucesso' && 'Pagamento concluído'}
              </p>
            </div>
          </div>

          {/* Indicador de etapas */}
          <div className="flex items-center gap-2 mt-4">
            <div
              className={`h-1 flex-1 rounded-full transition-all ${
                etapa === 'formulario' ? 'bg-white' : 'bg-white/40'
              }`}
            ></div>
            <div
              className={`h-1 flex-1 rounded-full transition-all ${
                etapa === 'token' ? 'bg-white' : 'bg-white/40'
              }`}
            ></div>
            <div
              className={`h-1 flex-1 rounded-full transition-all ${
                etapa === 'sucesso' ? 'bg-white' : 'bg-white/40'
              }`}
            ></div>
          </div>
        </div>

        <div className="p-6 overflow-y-auto flex-1 pb-20">
          {/* Etapa: Formulário */}
          {etapa === 'formulario' && (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Saldo Disponível */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="text-sm text-blue-700 mb-1">Pontos Disponíveis</div>
                <div className="text-2xl font-bold text-blue-600">
                  {saldo.toLocaleString('pt-BR')} pts
                </div>
              </div>

              {/* Seleção de Crédito */}
              <div>
                <label
                  htmlFor="credito-select"
                  className="block text-sm font-medium text-slate-700 mb-2"
                >
                  Selecione o Crédito para Resgate
                </label>
                <select
                  id="credito-select"
                  data-tour="pagamento-credito-select"
                  value={creditoSelecionado}
                  onChange={(e) => {
                    setCreditoSelecionado(e.target.value);
                    setDadosConta(null);
                  }}
                  required
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white cursor-pointer"
                >
                  <option value="">Selecione um crédito</option>
                  {creditosDisponiveis.map((credito) => (
                    <option key={credito.id} value={credito.id}>
                      {credito.descricao} - R${' '}
                      {credito.valor.toLocaleString('pt-BR', {
                        minimumFractionDigits: 2,
                      })}{' '}
                      ({credito.campanha})
                    </option>
                  ))}
                </select>

                {/* Detalhes do Crédito Selecionado */}
                {creditoAtual && (
                  <div className="mt-3 bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2">
                    <div className="flex items-center gap-2 text-blue-700 font-semibold text-sm">
                      <i className="ri-information-line"></i>
                      <span>Detalhes do Crédito</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-slate-600">Campanha:</span>
                        <p className="font-semibold text-slate-900">{creditoAtual.campanha}</p>
                      </div>
                      <div>
                        <span className="text-slate-600">Data:</span>
                        <p className="font-semibold text-slate-900">{creditoAtual.data}</p>
                      </div>
                    </div>
                    <div className="pt-2 border-t border-slate-200">
                      <span className="text-slate-600 text-sm">Valor Disponível:</span>
                      <p className="font-bold text-blue-600 text-lg">
                        R${' '}
                        {creditoAtual.valor.toLocaleString('pt-BR', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Tipo de Conta */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Tipo de Pagamento
                </label>
                <div className="grid grid-cols-2 gap-3" data-tour="pagamento-tipo">
                  <button
                    type="button"
                    onClick={() => {
                      setTipoConta('boleto');
                      setCodigoBarras('');
                      setDadosConta(null);
                    }}
                    className={`flex flex-col items-center gap-2 px-4 py-4 rounded-lg border-2 transition-all cursor-pointer ${
                      tipoConta === 'boleto'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'
                    }`}
                  >
                    <i className="ri-file-text-line text-2xl"></i>
                    <span className="text-sm font-medium">Boleto</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setTipoConta('consumo');
                      setCodigoBarras('');
                      setDadosConta(null);
                    }}
                    className={`flex flex-col items-center gap-2 px-4 py-4 rounded-lg border-2 transition-all cursor-pointer ${
                      tipoConta === 'consumo'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'
                    }`}
                  >
                    <i className="ri-lightbulb-line text-2xl"></i>
                    <span className="text-sm font-medium">Conta de Consumo</span>
                  </button>
                </div>
              </div>

              {/* Código de Barras */}
              <div>
                <label
                  htmlFor="codigo-barras"
                  className="block text-sm font-medium text-slate-700 mb-2"
                >
                  Código de Barras / Linha Digitável
                </label>
                <div className="space-y-2">
                  <input
                    id="codigo-barras"
                    data-tour="pagamento-codigo"
                    type="text"
                    value={codigoBarras}
                    onChange={handleCodigoChange}
                    placeholder="Digite ou cole o código"
                    maxLength={48}
                    required
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  />

                  {/* Botão de Scanner (simulado) */}
                  <button
                    type="button"
                    onClick={() => {
                      const scannerMsg = document.getElementById('scanner-msg');
                      if (scannerMsg) {
                        scannerMsg.classList.remove('hidden');
                        setTimeout(() => scannerMsg.classList.add('hidden'), 3000);
                      }
                    }}
                    className="w-full px-4 py-3 border-2 border-dashed border-slate-300 text-slate-600 rounded-lg hover:border-blue-500 hover:text-blue-600 transition-colors font-medium flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-camera-line text-xl"></i>
                    Escanear Código de Barras
                  </button>
                  <p id="scanner-msg" className="hidden text-xs text-amber-600 text-center">
                    <i className="ri-information-line mr-1"></i>
                    Funcionalidade de scanner em desenvolvimento
                  </p>

                  {/* Validar Código */}
                  {codigoBarras.length >= 44 && !dadosConta && creditoSelecionado && (
                    <button
                      type="button"
                      onClick={handleValidarCodigo}
                      disabled={validando}
                      data-tour="pagamento-btn-validar"
                      className={`w-full px-4 py-3 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 whitespace-nowrap ${
                        validando
                          ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                          : 'bg-blue-600 text-white hover:bg-blue-700 cursor-pointer'
                      }`}
                    >
                      {validando ? (
                        <>
                          <i className="ri-loader-4-line animate-spin"></i>
                          Validando...
                        </>
                      ) : (
                        <>
                          <i className="ri-search-line"></i>
                          Validar Código
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>

              {/* Dados da Conta Validada */}
              {dadosConta && (
                <div className="bg-slate-50 rounded-lg p-4 space-y-3 border-2 border-blue-200">
                  <div className="flex items-center gap-2 text-blue-700 font-semibold mb-2">
                    <i className="ri-check-line text-xl"></i>
                    <span>Dados da Conta</span>
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between items-start">
                      <span className="text-sm text-slate-600">Beneficiário</span>
                      <span className="font-semibold text-slate-900 text-right">
                        {dadosConta.beneficiario}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-600">Tipo</span>
                      <span className="font-semibold text-slate-900">{dadosConta.tipo}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-600">Vencimento</span>
                      <span className="font-semibold text-slate-900">
                        {new Date(dadosConta.vencimento).toLocaleDateString('pt-BR')}
                      </span>
                    </div>
                    <div className="flex justify-between items-center pt-2 border-t border-slate-200">
                      <span className="text-sm text-slate-600">Pagar conta de</span>
                      <span className="font-bold text-blue-600 text-xl">
                        R${' '}
                        {dadosConta.valor.toLocaleString('pt-BR', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-bold text-slate-700">Pontos necessários</span>
                      <span className="font-bold text-orange-600 text-lg">
                        {pontosNecessarios.toLocaleString('pt-BR')} pts
                      </span>
                    </div>
                  </div>

                  {/* Validação de Saldo */}
                  {!valorValido && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                      <i className="ri-error-warning-line text-red-600 text-lg mt-0.5"></i>
                      <div>
                        <p className="text-sm font-semibold text-red-800">Crédito Insuficiente</p>
                        <p className="text-xs text-red-600 mt-1">
                          O crédito selecionado não é suficiente para este pagamento. Selecione outro
                          crédito.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Botões */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={animatedClose}
                  className="flex-1 px-6 py-3 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors font-medium cursor-pointer whitespace-nowrap"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  data-tour="pagamento-btn-continuar"
                  disabled={!dadosConta || !valorValido}
                  className={`flex-1 px-6 py-3 rounded-lg font-semibold transition-all whitespace-nowrap ${
                    dadosConta && valorValido
                      ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white hover:shadow-lg cursor-pointer'
                      : 'bg-slate-200 text-slate-500 cursor-not-allowed'
                  }`}
                >
                  Continuar
                </button>
              </div>
            </form>
          )}

          {/* Etapa: Token de Confirmação */}
          {etapa === 'token' && dadosConta && creditoAtual && (
            <div className="space-y-5">
              {/* Resumo dos dados */}
              <div className="bg-slate-50 rounded-xl p-4 space-y-3">
                <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">
                  Resumo do Pagamento
                </h4>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-500">Crédito</span>
                  <span className="text-sm font-semibold text-slate-800">{creditoAtual.descricao}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-500">Campanha</span>
                  <span className="text-sm font-medium text-slate-700">{creditoAtual.campanha}</span>
                </div>
                <div className="flex justify-between items-start">
                  <span className="text-sm text-slate-500">Beneficiário</span>
                  <span className="text-sm font-semibold text-slate-800 text-right">
                    {dadosConta.beneficiario}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-500">Tipo</span>
                  <span className="text-sm font-medium text-slate-700">{dadosConta.tipo}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-500">Vencimento</span>
                  <span className="text-sm font-medium text-slate-700">
                    {new Date(dadosConta.vencimento).toLocaleDateString('pt-BR')}
                  </span>
                </div>
                <div className="flex justify-between items-center pt-2 border-t border-slate-200">
                  <span className="text-sm text-slate-500">Pagar conta de</span>
                  <span className="text-lg font-bold text-blue-600">
                    R${' '}
                    {dadosConta.valor.toLocaleString('pt-BR', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-bold text-slate-700">Pontos utilizados</span>
                  <span className="text-lg font-bold text-orange-600">
                    {pontosNecessarios.toLocaleString('pt-BR')} pts
                  </span>
                </div>
              </div>

              {/* Pontos Após Pagamento */}
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-orange-700">Pontos restantes após pagamento</span>
                  <span className="text-lg font-bold text-orange-600">
                    {Math.max(0, saldo - pontosNecessarios).toLocaleString('pt-BR')} pts
                  </span>
                </div>
              </div>

              {/* Token Section */}
              <div className="text-center space-y-4">
                <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mx-auto">
                  <i className="ri-shield-keyhole-line text-2xl text-amber-600"></i>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-slate-900">Token de Segurança</h4>
                  <p className="text-sm text-slate-500 mt-1">
                    Enviamos um código de 6 dígitos para seu e-mail e celular cadastrados
                  </p>
                </div>

                {/* Inputs do Token */}
                <div className="flex justify-center gap-2" onPaste={handleTokenPaste}>
                  {tokenDigits.map((digit, index) => (
                    <input
                      key={index}
                      ref={(el) => {
                        inputRefs.current[index] = el;
                      }}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleTokenChange(index, e.target.value)}
                      onKeyDown={(e) => handleTokenKeyDown(index, e)}
                      className={`w-12 h-14 text-center text-xl font-bold border-2 rounded-lg focus:outline-none transition-all ${
                        tokenErro
                          ? 'border-red-400 bg-red-50 text-red-600 focus:ring-2 focus:ring-red-300'
                          : digit
                          ? 'border-blue-400 bg-blue-50 text-blue-700 focus:ring-2 focus:ring-blue-300'
                          : 'border-slate-300 bg-white text-slate-800 focus:ring-2 focus:ring-blue-400 focus:border-blue-400'
                      }`}
                    />
                  ))}
                </div>

                {/* Erro do Token */}
                {tokenErro && (
                  <div className="flex items-center justify-center gap-2 text-red-600 bg-red-50 rounded-lg py-2 px-4">
                    <i className="ri-error-warning-line"></i>
                    <span className="text-sm font-medium">{tokenErro}</span>
                  </div>
                )}

                {/* Reenviar Token */}
                <div className="text-sm text-slate-500">
                  Não recebeu o código?{' '}
                  {reenvioTimer > 0 ? (
                    <span className="text-slate-400">
                      Reenviar em{' '}
                      <span className="font-semibold text-slate-600">{reenvioTimer}s</span>
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={handleReenviarToken}
                      className="text-blue-600 font-semibold hover:text-blue-700 hover:underline cursor-pointer"
                    >
                      Reenviar código
                    </button>
                  )}
                </div>
              </div>

              {/* Botões */}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setEtapa('formulario');
                    setTokenDigits(['', '', '', '', '', '']);
                    setTokenErro('');
                  }}
                  disabled={processando}
                  className="flex-1 px-6 py-3 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors font-medium disabled:opacity-50 cursor-pointer whitespace-nowrap"
                >
                  Voltar
                </button>
                <button
                  type="button"
                  onClick={handleConfirmarToken}
                  disabled={!tokenCompleto || processando}
                  data-tour="pagamento-btn-confirmar"
                  className={`flex-1 px-6 py-3 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 whitespace-nowrap ${
                    !tokenCompleto || processando
                      ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                      : 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white hover:shadow-lg cursor-pointer'
                  }`}
                >
                  {processando ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Validando...
                    </>
                  ) : (
                    <>
                      <i className="ri-shield-check-line"></i>
                      Confirmar Pagamento
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Etapa: Sucesso */}
          {etapa === 'sucesso' && dadosConta && (
            <div className="text-center space-y-6">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <i className="ri-check-line text-4xl text-green-600"></i>
              </div>

              <div>
                <h4 className="text-2xl font-bold text-slate-900 mb-2">Pagamento Realizado!</h4>
                <p className="text-slate-600">Sua conta foi paga com sucesso</p>
              </div>

              {/* Comprovante */}
              <div className="bg-slate-50 rounded-lg p-4 space-y-3 text-left">
                <div className="flex justify-between items-start">
                  <span className="text-sm text-slate-600">Beneficiário</span>
                  <span className="font-semibold text-slate-900 text-right">{dadosConta.beneficiario}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">Conta paga</span>
                  <span className="font-bold text-green-600 text-lg">
                    R${' '}
                    {dadosConta.valor.toLocaleString('pt-BR', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">Pontos utilizados</span>
                  <span className="font-bold text-orange-600 text-base">
                    {pontosNecessarios.toLocaleString('pt-BR')} pts
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">Data e Hora</span>
                  <span className="font-semibold text-slate-900">
                    {dataHoraTransacao.current || new Date().toLocaleString('pt-BR')}
                  </span>
                </div>
                <div className="flex justify-between items-start pt-2 border-t border-slate-200">
                  <span className="text-sm text-slate-600">Código</span>
                  <span className="font-mono text-xs text-slate-900 text-right break-all">
                    {dadosConta.documento.slice(0, 20)}...
                  </span>
                </div>
              </div>

              {/* Botões de Comprovante */}
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={gerarComprovantePDF}
                  disabled={salvandoPdf}
                  className="flex items-center justify-center gap-2 px-4 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors font-medium cursor-pointer whitespace-nowrap disabled:opacity-50"
                >
                  {salvandoPdf ? (
                    <i className="ri-loader-4-line animate-spin text-lg"></i>
                  ) : (
                    <i className="ri-file-pdf-2-line text-lg text-red-500"></i>
                  )}
                  <span className="text-sm">Salvar PDF</span>
                </button>
                <div className="relative">
                  <button
                    onClick={() => setShareMenuOpen(!shareMenuOpen)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors font-medium cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-share-forward-line text-lg text-blue-600"></i>
                    <span id="pgto-share-feedback" className="text-sm">
                      {copiado ? 'Copiado!' : 'Compartilhar'}
                    </span>
                  </button>

                  {/* Menu de Compartilhamento */}
                  {shareMenuOpen && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setShareMenuOpen(false)}></div>
                      <div className="absolute bottom-full right-0 mb-2 w-56 bg-white rounded-xl shadow-xl border border-slate-200 py-2 z-20 animate-fade-in">
                        <p className="px-4 py-1.5 text-xs font-semibold text-slate-400 uppercase tracking-wide">Compartilhar via</p>
                        <button
                          onClick={handleShareWhatsApp}
                          className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-green-50 transition-colors cursor-pointer"
                        >
                          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-green-500">
                            <i className="ri-whatsapp-line text-white text-base"></i>
                          </div>
                          <span className="text-sm font-medium text-slate-700">WhatsApp</span>
                        </button>
                        <button
                          onClick={handleShareTelegram}
                          className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-sky-50 transition-colors cursor-pointer"
                        >
                          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-sky-500">
                            <i className="ri-telegram-line text-white text-base"></i>
                          </div>
                          <span className="text-sm font-medium text-slate-700">Telegram</span>
                        </button>
                        <button
                          onClick={handleShareFacebook}
                          className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-blue-50 transition-colors cursor-pointer"
                        >
                          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#1877F2]">
                            <i className="ri-facebook-fill text-white text-base"></i>
                          </div>
                          <span className="text-sm font-medium text-slate-700">Facebook</span>
                        </button>
                        <button
                          onClick={handleShareTwitter}
                          className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 transition-colors cursor-pointer"
                        >
                          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-900">
                            <i className="ri-twitter-x-line text-white text-base"></i>
                          </div>
                          <span className="text-sm font-medium text-slate-700">X (Twitter)</span>
                        </button>
                        <button
                          onClick={handleShareLinkedIn}
                          className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-blue-50 transition-colors cursor-pointer"
                        >
                          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#0A66C2]">
                            <i className="ri-linkedin-fill text-white text-base"></i>
                          </div>
                          <span className="text-sm font-medium text-slate-700">LinkedIn</span>
                        </button>
                        <div className="border-t border-slate-100 my-1"></div>
                        <button
                          onClick={handleCopiarTexto}
                          className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 transition-colors cursor-pointer"
                        >
                          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-200">
                            <i className="ri-file-copy-line text-slate-600 text-base"></i>
                          </div>
                          <span className="text-sm font-medium text-slate-700">Copiar Texto</span>
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-700 flex items-start gap-2">
                  <i className="ri-information-line text-lg mt-0.5"></i>
                  <span>
                    O comprovante foi enviado para seu e-mail e está disponível no extrato.
                  </span>
                </p>
              </div>

              <button
                onClick={animatedClose}
                className="w-full px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg hover:shadow-lg transition-all font-semibold cursor-pointer whitespace-nowrap"
              >
                Fechar
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
